import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Package, MapPin, Edit3, AlertCircle } from 'lucide-react';
import { RemovalInstructionDisplay } from '@/components/removal-display/RemovalInstructionDisplay';
import { apiJson, apiFetch } from '@/lib/api';
import { dbg } from '@/lib/dbg';

export default function TaskDetail() {
  const queryClient = useQueryClient();
  
  // State for selection
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  
  // State for delivery modal
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [deliveryQuantities, setDeliveryQuantities] = useState<{[key: string]: number}>({});
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [deliveryNotes, setDeliveryNotes] = useState('');



  // Fetch retailers
  const { data: retailers } = useQuery({
    queryKey: ['/api/retailers'],
    queryFn: () => fetch('/api/retailers', { credentials: 'omit' }).then(res => res.json()),
    enabled: true
  });

  // Fetch cycles for selected retailer
  const { data: cycles, isLoading: cyclesLoading, error: cyclesError } = useQuery({
    queryKey: ['/api/retailers', selectedRetailerId, 'cycles'],
    queryFn: async () => {
      console.log('Fetching cycles for retailer:', selectedRetailerId);
      const data = await apiJson(`/api/retailers/${selectedRetailerId}/cycles`);
      console.log('Cycles API response:', data);
      return data;
    },
    enabled: !!selectedRetailerId
  });

  // Show all cycles returned for the retailer; we will mark locked in the UI
  const filteredCycles = useMemo(() => {
    if (!cycles || !selectedRetailerId) return [];
    return cycles as any[];
  }, [cycles, selectedRetailerId]);

  // Determine if the currently selected cycle is locked (for UI messaging)
  const isSelectedCycleLocked = useMemo(() => {
    if (!selectedCycleId || !cycles) return false;
    const idNum = parseInt(selectedCycleId);
    const found = (cycles as any[]).find((c: any) => c.id === idNum);
    return !!found?.locked_flag;
  }, [selectedCycleId, cycles]);

  // Fetch operators
  const { data: operators } = useQuery({
    queryKey: ['/api/users/operators'],
    queryFn: () =>
      apiJson<
        Array<{
          id: number;
          name: string | null;
          username: string;
          region: string | null;
          isActive: boolean;
          email: string | null;
        }>
      >('/api/users/operators'),
    enabled: true
  });

  // Get campaign data for format-level removal support
  const { data: campaigns = [] } = useQuery({
    queryKey: ['/api/campaigns'],
    queryFn: async () => {
      const response = await fetch('/api/campaigns', {
        credentials: 'omit'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }
      return response.json();
    },
    staleTime: 300000, // Cache for 5 minutes
  });

  // Helper function to get campaign information for format-level removal support
  const getCampaignInfo = (campaignId: number) => {
    return campaigns.find((c: any) => c.id === campaignId) || {};
  };

  // Fetch stores for selected operator
  const { data: stores } = useQuery({
    queryKey: ['/api/stores/operator', selectedOperatorId],
    queryFn: async () => {
      console.log('[TASK_DETAIL_DEBUG] Fetching stores for operator:', selectedOperatorId);
      try {
        const result = await apiJson<Array<{ id: number; name: string }>>(
          `/api/stores/operator/${selectedOperatorId}`,
        );
        console.log('[TASK_DETAIL_DEBUG] Stores response:', result, 'isArray:', Array.isArray(result));
        return Array.isArray(result) ? result : [];
      } catch (err) {
        console.warn('[TASK_DETAIL_DEBUG] Stores API failed:', err);
        return [];
      }
    },
    enabled: !!selectedOperatorId
  });

  // Auto-select first store alphabetically when stores load
  useEffect(() => {
    if (stores && stores.length > 0 && !selectedStoreId) {
      const sortedStores = [...stores].sort((a, b) => a.name.localeCompare(b.name));
      setSelectedStoreId(sortedStores[0].id.toString());
    }
  }, [stores, selectedStoreId]);

  // Fetch assignments for selected criteria (works for any cycle via canonical endpoint)
  const { data: assignments, isLoading: assignmentsLoading, error: assignmentsError } = useQuery({
    queryKey: ['/api/assignments', selectedRetailerId, selectedCycleId, selectedOperatorId],
    queryFn: async () => {
      // Always try locked-cycles endpoint first; fall back if 404
      try {
        dbg.log('[ASSIGNMENTS_FETCH] trying locked-cycles first');
        const lockedResp = await apiFetch(`/api/locked-cycles/${selectedCycleId}/assignments`);
        if (lockedResp.ok) {
          const body = await lockedResp.json();
          const lockedRows: any[] = Array.isArray(body)
            ? body
            : (Array.isArray(body?.assignments) ? body.assignments : (Array.isArray(body?.rows) ? body.rows : []));
          let normalizedLocked = (lockedRows || []).map((a: any) => ({
            ...a,
            store: a.store || { name: a.storeName, address: a.storeAddress },
            campaign: a.campaign || { name: a.campaignName, description: '' },
            mediaRequirements: Array.isArray(a.mediaRequirements) ? a.mediaRequirements : [],
          }));
          // Optional client-side filtering by operator/retailer if provided
          if (selectedOperatorId) {
            normalizedLocked = normalizedLocked.filter((a: any) => `${a.operatorId || a.operator_id}` === selectedOperatorId);
          }
          if (selectedRetailerId) {
            normalizedLocked = normalizedLocked.filter((a: any) => {
              const rid = a.retailerId || a.retailer_id;
              return rid ? `${rid}` === `${selectedRetailerId}` : true;
            });
          }
          dbg.log('[ASSIGNMENTS_FETCH] locked normalized', normalizedLocked.length);
          return normalizedLocked;
        }
        if (lockedResp.status !== 404) {
          // For non-404 errors, throw to surface diagnostics card
          const txt = await lockedResp.text().catch(() => '');
          throw new Error(`/api/locked-cycles/${selectedCycleId}/assignments failed: ${lockedResp.status} ${txt}`);
        }
      } catch (e) {
        // Continue to fallback
        dbg.warn?.('[ASSIGNMENTS_FETCH] locked-cycles attempt failed, falling back', (e as any)?.message);
      }

      const params = new URLSearchParams();
      if (selectedCycleId) params.append('cycleId', selectedCycleId);
      if (selectedRetailerId) params.append('retailerId', selectedRetailerId);
      if (selectedOperatorId) params.append('operatorId', selectedOperatorId);

      dbg.log('[ASSIGNMENTS_FETCH] using canonical /api/assignments with params', params.toString());
      const resp = await apiJson<any>(`/api/admin/assignments?${params.toString()}`);
      const rows: any[] = Array.isArray(resp)
        ? resp
        : (Array.isArray(resp?.assignments) ? resp.assignments : (Array.isArray(resp?.rows) ? resp.rows : []));

      // Normalize minimal fields expected by renderer
      const normalized = rows.map((a: any) => ({
        ...a,
        store: { name: a.storeName, address: a.storeAddress },
        campaign: { name: a.campaignName, description: '' },
        mediaRequirements: Array.isArray(a.mediaRequirements) ? a.mediaRequirements : [],
      }));

      dbg.log('[ASSIGNMENTS_FETCH] normalized', normalized.length);
      return normalized;
    },
    enabled: !!selectedCycleId, // render for any cycle, not only locked
  });

  // Fetch campaign tasks for assignments
  const { data: campaignTasks } = useQuery({
    queryKey: ['/api/campaign-tasks'],
    queryFn: () => fetch('/api/campaign-tasks', { credentials: 'omit' }).then(res => res.json()),
    enabled: false // Disable this query since assignments already include posting records
  });

  // Group assignments by store
  const groupedAssignments = useMemo(() => {
    if (!assignments || !Array.isArray(assignments)) return {};
    
    return assignments.reduce((groups: any, assignment: any) => {
      const storeKey = assignment.storeId;
      if (!groups[storeKey]) {
        groups[storeKey] = {
          store: assignment.store,
          assignments: []
        };
      }
      groups[storeKey].assignments.push(assignment);
      return groups;
    }, {});
  }, [assignments]);

  // Helper function to handle delivery submission
  const handleDeliverySubmit = async () => {
    if (!selectedAssignment) return;
    
    try {
      const formData = new FormData();
      formData.append('campaignId', selectedAssignment.campaignId.toString());
      formData.append('storeId', selectedAssignment.storeId.toString());
      formData.append('operatorId', selectedOperatorId);
      formData.append('taskId', selectedAssignment.taskId.toString());
      formData.append('mediaFormatQuantities', JSON.stringify(deliveryQuantities));
      formData.append('notes', deliveryNotes);

      // Add photos if any
      selectedPhotos.forEach((photo, index) => {
        formData.append(`photos`, photo);
      });

      const response = await fetch('/api/deliveries', {
        method: 'POST',
        body: formData,
        credentials: 'omit'
      });
      
      if (response.ok) {
        // Refresh assignments data
        queryClient.invalidateQueries({ queryKey: ['/api/assignments'] });
        queryClient.invalidateQueries({ queryKey: ['/api/campaign-tasks'] });
        setDeliveryModalOpen(false);
        setSelectedAssignment(null);
        setDeliveryQuantities({});
        setSelectedPhotos([]);
        setDeliveryNotes("");
      }
    } catch (error) {
      console.error('Failed to record delivery:', error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Task Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Operator Task Assignment Management
          </CardTitle>
          <CardDescription>
            Select retailer, cycle, and operator to review and manage the assignment of tasks to operators.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="retailer">Retailer</Label>
              <Select 
                value={selectedRetailerId} 
                onValueChange={(value) => {
                  setSelectedRetailerId(value);
                  setSelectedCycleId('');
                  setSelectedOperatorId('');
                  setSelectedTaskId('');
                }}
              >
                <SelectTrigger id="retailer">
                  <SelectValue placeholder="Select retailer" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {(retailers || []).map((retailer: any) => (
                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cycle">Cycle</Label>
              <Select 
                value={selectedCycleId} 
                onValueChange={(value) => {
                  setSelectedCycleId(value);
                  setSelectedOperatorId('');
                  setSelectedTaskId('');
                }}
                disabled={!selectedRetailerId}
              >
                <SelectTrigger id="cycle">
                  <SelectValue placeholder="Select cycle" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {(filteredCycles || []).map((cycle: any) => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}{cycle.locked_flag ? ' (Locked)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="operator">Operator</Label>
              <Select 
                value={selectedOperatorId} 
                onValueChange={(value) => {
                  setSelectedOperatorId(value);
                  setSelectedStoreId('');
                  setSelectedTaskId('');
                }}
                disabled={!selectedCycleId}
              >
                <SelectTrigger id="operator">
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {(operators || []).map((operator) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name} (@{operator.username})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="store">Store (Optional)</Label>
              <Select 
                value={selectedStoreId} 
                onValueChange={(value) => {
                  setSelectedStoreId(value);
                  setSelectedTaskId('');
                }}
                disabled={!selectedOperatorId}
              >
                <SelectTrigger id="store">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {Array.isArray(stores) ? stores.sort((a: any, b: any) => a.name?.localeCompare(b.name) || 0).map((store: any) => (
                    <SelectItem key={store.id} value={store.id.toString()}>
                      {store.name}
                    </SelectItem>
                  )) : []}
                  <SelectItem value="all">All stores</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diagnostics & Results */}
      {selectedRetailerId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Selection & Diagnostics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>Retailer ID: {selectedRetailerId || '—'}</div>
              <div>Cycle ID: {selectedCycleId || '—'} {isSelectedCycleLocked ? '(Locked)' : ''}</div>
              <div>Operator ID: {selectedOperatorId || '—'}</div>
              <div>Assignments: {Array.isArray(assignments) ? assignments.length : (assignmentsLoading ? 'loading…' : '—')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {selectedCycleId && assignmentsLoading && (
        <Card>
          <CardHeader>
            <CardTitle>Loading assignments…</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Fetching data for the selected filters.</p>
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {selectedCycleId && assignmentsError && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Failed to load assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700">{(assignmentsError as any)?.message || 'Unknown error'}</p>
          </CardContent>
        </Card>
      )}

      {/* Tasks Table or Empty States */}
      {!selectedRetailerId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Select a retailer to begin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Choose a retailer to see its booking cycles and assignments.</p>
          </CardContent>
        </Card>
      )}

      {selectedRetailerId && !selectedCycleId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Select a cycle to continue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">All cycles are shown. Locked cycles are marked with “(Locked)”.</p>
          </CardContent>
        </Card>
      )}

      {/* Removed locked-only restriction: we now support any cycle */}

      {assignments && Array.isArray(assignments) && assignments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Tasks ({assignments.length})
            </CardTitle>
            <CardDescription>
              Tasks for {(operators || []).find((o) => o.id.toString() === selectedOperatorId)?.name} in {(retailers || []).find((r: any) => r.id.toString() === selectedRetailerId)?.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Group assignments by store */}
              {Object.entries(groupedAssignments).map(([storeKey, group]: [string, any]) => (
                <div key={storeKey} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <div>
                        <h3 className="font-semibold text-lg">{group.store?.name || 'Unknown Store'}</h3>
                        <p className="text-sm text-gray-500">{group.store?.address}</p>
                      </div>
                    </div>
                    <Badge variant="secondary">{group.assignments.length} tasks</Badge>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-medium">Campaign</th>
                          <th className="text-left p-3 font-medium">Task</th>
                          <th className="text-left p-3 font-medium">Media Format</th>
                          <th className="text-left p-3 font-medium">Expected</th>
                          <th className="text-left p-3 font-medium">Delivered</th>
                          <th className="text-left p-3 font-medium">Status</th>
                          <th className="text-left p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.assignments.map((assignment: any) => {
                          const expectedTotal = assignment.mediaRequirements?.reduce((sum: number, req: any) => sum + req.quantity, 0) || 0;
                          const deliveredTotal = assignment.postingRecord?.postedUnits || 0;
                          const hasDiscrepancy = deliveredTotal !== expectedTotal;
                          
                          // If no media requirements, show as single row
                          if (!assignment.mediaRequirements || assignment.mediaRequirements.length === 0) {
                            return (
                              <tr key={assignment.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  {(() => {
                                    const campaign = getCampaignInfo(assignment.campaignId);
                                    const isRemoval = campaign.campaignType === 'removal';
                                    
                                    if (isRemoval && assignment.formatId && assignment.expectedCount) {
                                      return (
                                        <div>
                                          <div className="font-medium text-red-700">{assignment.campaign?.name || 'Unknown Campaign'}</div>
                                          <RemovalInstructionDisplay
                                            task={{
                                              formatId: assignment.formatId,
                                              expectedCount: assignment.expectedCount
                                            }}
                                            campaign={campaign}
                                            className="text-xs mt-1"
                                            variant="compact"
                                          />
                                        </div>
                                      );
                                    } else if (isRemoval) {
                                      return (
                                        <div>
                                          <div className="font-medium text-red-700">{assignment.campaign?.name || 'Unknown Campaign'}</div>
                                          <div className="text-xs text-red-600 mt-1">Removal Campaign</div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div>
                                          <div className="font-medium">{assignment.campaign?.name || 'Unknown Campaign'}</div>
                                          <div className="text-sm text-gray-500">{assignment.campaign?.description}</div>
                                        </div>
                                      );
                                    }
                                  })()}
                                </td>
                                <td className="p-3">
                                  {assignment.taskName ? (
                                    <div className="font-medium">{assignment.taskName}</div>
                                  ) : (
                                    <Badge variant="destructive" className="text-xs">No task mapping</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className="text-xs">No media requirements</Badge>
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className="text-sm">{expectedTotal} units</Badge>
                                </td>
                                <td className="p-3">
                                  {assignment.postingRecord ? (
                                    <Badge variant={hasDiscrepancy ? "destructive" : "secondary"} className="text-sm">
                                      {deliveredTotal} units
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-sm text-gray-500">Not recorded</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {assignment.postingRecord ? (
                                    <Badge variant={hasDiscrepancy ? "destructive" : "secondary"}>
                                      {hasDiscrepancy ? 'DISCREPANCY' : 'COMPLETE'}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline">PENDING</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  <Button
                                    onClick={() => {
                                      setSelectedAssignment(assignment);
                                      setDeliveryModalOpen(true);
                                      setDeliveryQuantities({});
                                      setSelectedPhotos([]);
                                      setDeliveryNotes("");
                                    }}
                                    size="sm"
                                    variant="outline"
                                  >
                                    Record Delivery
                                  </Button>
                                </td>
                              </tr>
                            );
                          }
                          
                          // Show one row per media format
                          console.log('Assignment data:', assignment); // Debug
                          const mediaReqs = assignment.mediaRequirements || [];
                          if (mediaReqs.length === 0) {
                            return (
                              <tr key={assignment.id} className="border-b hover:bg-gray-50">
                                <td className="p-3 border-r">
                                  <div className="font-medium">{assignment.campaign?.name || assignment.campaignName || 'Unknown Campaign'}</div>
                                </td>
                                <td className="p-3 border-r">
                                  <div className="font-medium">{assignment.taskName || 'Unknown Task'}</div>
                                </td>
                                <td className="p-3 border-r">—</td>
                                <td className="p-3 border-r">—</td>
                                <td className="p-3 border-r">—</td>
                                <td className="p-3">—</td>
                                <td className="p-3">—</td>
                              </tr>
                            );
                          }
                          return mediaReqs.map((req: any, index: number) => {
                            // Parse delivered quantities from posting record notes
                            let deliveredForFormat = 0;
                            if (assignment.postingRecord?.notes) {
                              const formatMatch = assignment.postingRecord.notes.match(new RegExp(`MediaFormat ${req.mediaFormatId}: (\\d+) units`));
                              if (formatMatch) {
                                deliveredForFormat = parseInt(formatMatch[1]) || 0;
                              }
                            }
                            
                            const hasFormatDiscrepancy = deliveredForFormat !== req.quantity;
                            
                            // Check if there's a specific discrepancy for this format
                            const formatDiscrepancy = assignment.postingRecord?.discrepancies?.find((d: any) => 
                              d.notes.includes(req.mediaFormat?.name || 'Unknown')
                            );
                            
                            return (
                              <tr key={`${assignment.id}-${index}`} className="border-b hover:bg-gray-50">
                                {/* Campaign info only on first row */}
                                {index === 0 && (
                                  <td className="p-3 border-r" rowSpan={assignment.mediaRequirements.length}>
                                    <div>
                                      <div className="font-medium">{assignment.campaign?.name || assignment.campaignName || 'Unknown Campaign'}</div>
                                      <div className="text-sm text-gray-500">{assignment.campaign?.description || ''}</div>
                                    </div>
                                  </td>
                                )}
                                
                                {/* Task info only on first row */}
                                {index === 0 && (
                                  <td className="p-3 border-r" rowSpan={assignment.mediaRequirements.length}>
                                    {assignment.taskName ? (
                                      <div className="font-medium">{assignment.taskName}</div>
                                    ) : (
                                      <Badge variant="destructive" className="text-xs">No task mapping</Badge>
                                    )}
                                  </td>
                                )}
                                
                                {/* Media format - each gets its own row */}
                                <td className="p-3 border-r">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{req.mediaFormat?.name || req.mediaFormatName || 'Unknown Format'}</span>
                                    {req.groupLogic === 'OR' && index > 0 && (
                                      <Badge variant="outline" className="text-xs text-orange-600">OR</Badge>
                                    )}
                                  </div>
                                </td>
                                
                                {/* Expected quantity */}
                                <td className="p-3 border-r">
                                  <Badge variant="outline" className="text-sm">
                                    {req.quantity || req.expected || req.expectedQuantity || 0}
                                  </Badge>
                                </td>
                                
                                {/* Delivered quantity */}
                                <td className="p-3 border-r">
                                  {assignment.postingRecord ? (
                                    <div className="flex items-center gap-2">
                                      <Badge 
                                        variant={hasFormatDiscrepancy ? "destructive" : "secondary"}
                                        className="text-sm"
                                      >
                                        {deliveredForFormat}
                                      </Badge>
                                      {hasFormatDiscrepancy && (
                                        <span className="text-red-600 text-sm font-medium">
                                          ({deliveredForFormat - req.quantity > 0 ? '+' : ''}{deliveredForFormat - req.quantity})
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-sm text-gray-500">-</Badge>
                                  )}
                                </td>
                                
                                {/* Status */}
                                <td className="p-3 border-r">
                                  {assignment.postingRecord ? (
                                    <div className="flex flex-col gap-1">
                                      <Badge variant={hasFormatDiscrepancy ? "destructive" : "secondary"}>
                                        {hasFormatDiscrepancy ? 'DISCREPANCY' : 'COMPLETE'}
                                      </Badge>
                                      {formatDiscrepancy && (
                                        <Badge variant="outline" className="text-xs text-orange-600">
                                          {formatDiscrepancy.reasonCode}
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="outline">PENDING</Badge>
                                  )}
                                </td>
                                
                                {/* Actions only on first row */}
                                {index === 0 && (
                                  <td className="p-3" rowSpan={assignment.mediaRequirements.length}>
                                    <Button
                                      onClick={() => {
                                        setSelectedAssignment(assignment);
                                        setDeliveryModalOpen(true);
                                        // Pre-populate with expected quantities
                                        const initialQuantities: {[key: string]: number} = {};
                                        assignment.mediaRequirements.forEach((req: any) => {
                                          initialQuantities[req.mediaFormatId] = req.quantity;
                                        });
                                        setDeliveryQuantities(initialQuantities);
                                        setSelectedPhotos([]);
                                        setDeliveryNotes("");
                                      }}
                                      size="sm"
                                      variant="outline"
                                    >
                                      Record Delivery
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {assignments && Array.isArray(assignments) && assignments.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-gray-600" />
              No assignments found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">No tasks are assigned for the selected cycle and filters.</p>
          </CardContent>
        </Card>
      )}

      {/* Delivery Modal */}
      <Dialog open={deliveryModalOpen} onOpenChange={setDeliveryModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Record Delivery</DialogTitle>
          </DialogHeader>
          
          {selectedAssignment && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium">{selectedAssignment.campaign?.name}</h3>
                <p className="text-sm text-gray-600">{selectedAssignment.taskName}</p>
                <p className="text-sm text-gray-600">{selectedAssignment.store?.name}</p>
              </div>
              
              {selectedAssignment.mediaRequirements && selectedAssignment.mediaRequirements.length > 0 ? (
                <div className="space-y-3">
                  <Label className="text-base font-medium">Media Format Quantities</Label>
                  {selectedAssignment.mediaRequirements.map((req: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">{req.mediaFormat?.name || 'Unknown Format'}</div>
                        <div className="text-sm text-gray-500">Expected: {req.quantity}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`quantity-${req.mediaFormatId}`} className="text-sm">Delivered:</Label>
                        <Input
                          id={`quantity-${req.mediaFormatId}`}
                          type="number"
                          min="0"
                          className="w-20"
                          value={deliveryQuantities[req.mediaFormatId] || 0}
                          onChange={(e) => setDeliveryQuantities({
                            ...deliveryQuantities,
                            [req.mediaFormatId]: parseInt(e.target.value) || 0
                          })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No media requirements defined</div>
              )}
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Optional delivery notes..."
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Photos (Optional)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setSelectedPhotos(files);
                  }}
                />
                {selectedPhotos.length > 0 && (
                  <div className="text-sm text-gray-600">
                    {selectedPhotos.length} photo(s) selected
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeliveryModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleDeliverySubmit}>
                  Record Delivery
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
